import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { KeyRound, Pencil, Plus, Send, Trash2, Copy, RefreshCw } from 'lucide-react';
import { api, type InvitationRow, type MemberRow, type TenantDetail } from '../api';
import { authClient } from '../auth-client';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Skeleton } from '../components/ui/skeleton';
import { EmptyState, ErrorNote, Field, PageHeader, Panel } from '../components/kit';

/**
 * One tenant at a time, its settings split into tabs. Everything a tenant needs
 * to run — repositories, tracker, handlers, git access, people — is here, each
 * section saying plainly what it does.
 */
export function TenantsPage({ tenant, isAdmin }: { tenant: string | null; isAdmin: boolean }) {
  const client = useQueryClient();
  const tenants = useQuery({ queryKey: ['tenants'], queryFn: () => api.get<TenantDetail[]>('/api/tenants') });
  const refresh = () => client.invalidateQueries({ queryKey: ['tenants'] });
  const [creating, setCreating] = useState(false);

  const visible = (tenants.data ?? []).filter((entry) => !tenant || entry.id === tenant);
  const [selected, setSelected] = useState<string | null>(null);
  const active = visible.find((entry) => entry.id === (selected ?? tenant)) ?? visible[0];

  return (
    <>
      <PageHeader
        title="Tenants"
        description="A tenant is an isolation boundary: its own repositories, tracker, agent settings, git key and people. Nothing crosses between them."
        actions={
          isAdmin ? (
            <Button onClick={() => setCreating(true)}>
              <Plus className="size-3.5" aria-hidden /> New tenant
            </Button>
          ) : undefined
        }
      />

      {tenants.isPending && <Skeleton className="h-40 w-full" />}

      {tenants.isFetched && visible.length === 0 && (
        <EmptyState
          title="No tenants yet"
          hint="Create one to hold your repositories, tracker connection and agent settings."
          action={
            isAdmin ? (
              <Button onClick={() => setCreating(true)}>
                <Plus className="size-3.5" aria-hidden /> New tenant
              </Button>
            ) : undefined
          }
        />
      )}

      {visible.length > 1 && (
        <div className="mb-6 flex flex-wrap gap-2">
          {visible.map((entry) => (
            <Button
              key={entry.id}
              variant={active?.id === entry.id ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSelected(entry.id)}
            >
              {entry.name}
            </Button>
          ))}
        </div>
      )}

      {active && (
        <Tabs defaultValue="repositories" key={active.id}>
          <TabsList>
            <TabsTrigger value="repositories">Repositories</TabsTrigger>
            <TabsTrigger value="sources">Tracker</TabsTrigger>
            <TabsTrigger value="handlers">Handlers</TabsTrigger>
            <TabsTrigger value="agent">Agent</TabsTrigger>
            <TabsTrigger value="access">Access</TabsTrigger>
          </TabsList>

          <TabsContent value="repositories" className="mt-5">
            <Repositories tenant={active} onChanged={refresh} />
          </TabsContent>
          <TabsContent value="sources" className="mt-5">
            <Sources tenant={active} onChanged={refresh} />
          </TabsContent>
          <TabsContent value="handlers" className="mt-5">
            <Handlers tenant={active} onChanged={refresh} />
          </TabsContent>
          <TabsContent value="agent" className="mt-5">
            <AgentSettings tenant={active} onChanged={refresh} />
          </TabsContent>
          <TabsContent value="access" className="mt-5">
            <DeployKey tenant={active} onChanged={refresh} />
            <Members tenant={active} />
          </TabsContent>
        </Tabs>
      )}

      <NewTenantDialog open={creating} onClose={() => setCreating(false)} onCreated={refresh} />
    </>
  );
}

function NewTenantDialog({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const create = useMutation({
    mutationFn: async () => {
      const result = await authClient.organization.create({ name, slug });
      if (result.error) throw new Error(result.error.message ?? 'could not create tenant');
      return result.data;
    },
    onSuccess: () => {
      setName('');
      setSlug('');
      onCreated();
      onClose();
    },
  });

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New tenant</DialogTitle>
          <DialogDescription>
            A separate world: its own repositories, tracker, agent settings, deploy key and members.
          </DialogDescription>
        </DialogHeader>
        <form
          className="grid gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            create.mutate();
          }}
        >
          <div className="grid content-start gap-1.5">
            <Label htmlFor="tenant-name">Name</Label>
            <Input
              id="tenant-name"
              required
              value={name}
              onChange={(event) => {
                setName(event.target.value);
                setSlug(event.target.value.toLowerCase().replace(/[^a-z0-9-]+/g, '-'));
              }}
            />
          </div>
          <div className="grid content-start gap-1.5">
            <Label htmlFor="tenant-slug">Slug</Label>
            <Input id="tenant-slug" required value={slug} onChange={(event) => setSlug(event.target.value)} />
          </div>
          <ErrorNote error={create.error} />
          <div>
            <Button type="submit" disabled={create.isPending}>
              Create tenant
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const SELECT =
  'h-8 rounded-[5px] border border-rule bg-field px-2.5 text-[13px] text-ink outline-none transition-[border-color,box-shadow] hover:border-rule-strong focus-visible:border-brand focus-visible:ring-2 focus-visible:ring-brand/25';

/**
 * A list is a list. Editing a row opens the same dialog that adds one, so the
 * fields appear where the click did instead of filling a form far below the
 * table — and "new" and "editing" can never look like the same thing.
 */
function Repositories({ tenant, onChanged }: { tenant: TenantDetail; onChanged: () => void }) {
  const empty = { id: '', key: '', repoUrl: '', baseBranch: 'main', createMr: 'api', afterCreate: '' };
  const [draft, setDraft] = useState<typeof empty | null>(null);
  const editing = Boolean(draft?.id);

  const save = useMutation({
    mutationFn: () =>
      api.post(`/api/tenants/${tenant.id}/projects`, {
        ...draft,
        id: draft?.id || undefined,
        createMr: draft?.createMr || null,
        afterCreate: draft?.afterCreate || null,
      }),
    onSuccess: () => {
      setDraft(null);
      onChanged();
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.del(`/api/tenants/${tenant.id}/projects/${id}`),
    onSuccess: onChanged,
  });

  return (
    <Panel
      title="Repositories"
      description="Where the agent works. Give a clone URL — taskpilot decides the checkout location and clones on first use."
      actions={
        <Button size="sm" onClick={() => setDraft(empty)}>
          <Plus className="size-3.5" aria-hidden /> Add repository
        </Button>
      }
    >
      {tenant.projects.length === 0 ? (
        <EmptyState
          title="No repositories"
          hint="A task cannot run without one."
          action={
            <Button size="sm" onClick={() => setDraft(empty)}>
              <Plus className="size-3.5" aria-hidden /> Add repository
            </Button>
          }
        />
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Key</TableHead>
              <TableHead>Clone URL</TableHead>
              <TableHead className="hidden md:table-cell">Base</TableHead>
              <TableHead className="hidden md:table-cell">On success</TableHead>
              <TableHead className="hidden lg:table-cell">Checkout</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {tenant.projects.map((project) => (
              <TableRow key={project.id}>
                <TableCell className="data font-semibold text-ink">{project.key}</TableCell>
                <TableCell className="max-w-[22rem] truncate data" title={project.repoUrl ?? ''}>
                  {project.repoUrl}
                </TableCell>
                <TableCell className="data hidden md:table-cell">
                  {project.remote}/{project.baseBranch}
                </TableCell>
                <TableCell className="hidden md:table-cell">
                  <Badge variant={project.push === false ? 'secondary' : project.createMr ? 'ok' : 'warn'}>
                    {project.push === false
                      ? 'keep local'
                      : project.createMr
                        ? 'push + merge request'
                        : 'push only'}
                  </Badge>
                </TableCell>
                <TableCell
                  className="hidden max-w-[18rem] truncate data text-ink-3 lg:table-cell"
                  title={project.checkoutPath}
                >
                  {project.checkoutPath}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      title={`Edit ${project.key}`}
                      onClick={() =>
                        setDraft({
                          id: project.id,
                          key: project.key,
                          repoUrl: project.repoUrl ?? '',
                          baseBranch: project.baseBranch,
                          createMr: project.createMr ?? '',
                          afterCreate: project.afterCreate ?? '',
                        })
                      }
                    >
                      <Pencil className="size-3.5" aria-hidden />
                      <span className="sr-only">Edit {project.key}</span>
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      title={`Remove ${project.key}`}
                      onClick={() => remove.mutate(project.id)}
                    >
                      <Trash2 className="size-3.5" aria-hidden />
                      <span className="sr-only">Remove {project.key}</span>
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      <ErrorNote error={remove.error} />

      <Dialog open={draft !== null} onOpenChange={(next) => !next && setDraft(null)}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editing ? `Edit ${draft?.key}` : 'New repository'}</DialogTitle>
            <DialogDescription>
              The agent works in a throwaway worktree of this repository, never in the checkout itself.
            </DialogDescription>
          </DialogHeader>
          {draft && (
            <form
              className="grid items-start gap-x-4 gap-y-3 sm:grid-cols-2"
              onSubmit={(event) => {
                event.preventDefault();
                save.mutate();
              }}
            >
              <Field label="Key" htmlFor="repo-key" hint="How a task names this repository.">
                <Input
                  id="repo-key"
                  required
                  autoFocus
                  placeholder="PORTAL"
                  value={draft.key}
                  onChange={(event) => setDraft({ ...draft, key: event.target.value.toUpperCase() })}
                />
              </Field>
              <Field label="Base branch" htmlFor="repo-branch" hint="Branches are cut from it.">
                <Input
                  id="repo-branch"
                  value={draft.baseBranch}
                  onChange={(event) => setDraft({ ...draft, baseBranch: event.target.value })}
                />
              </Field>
              <Field label="Clone URL" htmlFor="repo-url" className="sm:col-span-2">
                <Input
                  id="repo-url"
                  required
                  placeholder="git@github.com:org/repo.git"
                  value={draft.repoUrl}
                  onChange={(event) => setDraft({ ...draft, repoUrl: event.target.value })}
                />
              </Field>
              <Field
                label="Setup command"
                htmlFor="repo-setup"
                className="sm:col-span-2"
                hint="Runs once in each new worktree, before the agent starts. A non-zero exit fails the run before any tokens are spent."
              >
                <Input
                  id="repo-setup"
                  placeholder="npm ci"
                  value={draft.afterCreate}
                  onChange={(event) => setDraft({ ...draft, afterCreate: event.target.value })}
                />
              </Field>
              <Field
                label="When the run succeeds"
                htmlFor="repo-mr"
                className="sm:col-span-2"
                hint="Opening the request uses this tenant's API token. taskpilot never merges."
              >
                <select
                  id="repo-mr"
                  value={draft.createMr}
                  onChange={(event) => setDraft({ ...draft, createMr: event.target.value })}
                  className={SELECT}
                >
                  <option value="api">push the branch and open a merge request</option>
                  <option value="">push the branch only</option>
                </select>
              </Field>
              <div className="sm:col-span-2">
                <ErrorNote error={save.error} />
              </div>
              <div className="flex justify-end gap-2 border-t border-rule pt-3 sm:col-span-2">
                <Button type="button" variant="outline" onClick={() => setDraft(null)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={save.isPending}>
                  {editing ? 'Save changes' : 'Add repository'}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </Panel>
  );
}

function Sources({ tenant, onChanged }: { tenant: TenantDetail; onChanged: () => void }) {
  const empty = { id: '', name: '', baseUrl: '', email: '', jql: '', browseJql: '', plainSecret: '' };
  const [draft, setDraft] = useState<typeof empty | null>(null);
  const editing = Boolean(draft?.id);
  const save = useMutation({
    mutationFn: () =>
      api.post(`/api/tenants/${tenant.id}/sources`, {
        id: draft?.id || undefined,
        type: 'jira',
        name: draft?.name,
        config: {
          baseUrl: draft?.baseUrl,
          email: draft?.email,
          jql: draft?.jql,
          browseJql: draft?.browseJql,
        },
        // An empty field keeps the stored token rather than wiping it.
        plainSecret: draft?.plainSecret || undefined,
      }),
    onSuccess: () => {
      setDraft(null);
      onChanged();
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.del(`/api/tenants/${tenant.id}/sources/${id}`),
    onSuccess: onChanged,
  });
  const sync = useMutation({ mutationFn: () => api.post(`/api/sources/sync?tenant=${tenant.id}`) });

  return (
    <Panel
      title="Tracker"
      description="Jira issues become tasks. The queue query decides what runs unattended; the picker query decides what you can start by hand."
      actions={
        <>
          <Button variant="outline" size="sm" onClick={() => sync.mutate()} disabled={sync.isPending}>
            <RefreshCw className="size-3.5" aria-hidden /> Import now
          </Button>
          <Button size="sm" onClick={() => setDraft(empty)}>
            <Plus className="size-3.5" aria-hidden /> Add tracker
          </Button>
        </>
      }
    >
      {tenant.sources.length === 0 ? (
        <EmptyState
          title="No tracker connected"
          hint="Optional — tasks can still be queued by hand from the Runs screen."
          action={
            <Button size="sm" onClick={() => setDraft(empty)}>
              <Plus className="size-3.5" aria-hidden /> Add tracker
            </Button>
          }
        />
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Site</TableHead>
              <TableHead className="hidden md:table-cell">Queue query</TableHead>
              <TableHead>Token</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {tenant.sources.map((source) => (
              <TableRow key={source.id}>
                <TableCell className="data font-semibold text-ink">{source.name}</TableCell>
                <TableCell className="max-w-[16rem] truncate data">
                  {String(source.config.baseUrl ?? '')}
                </TableCell>
                <TableCell className="data hidden max-w-[20rem] truncate md:table-cell">
                  {String(source.config.jql ?? '')}
                </TableCell>
                <TableCell className="data text-ink-2">{source.secret || 'not set'}</TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      title={`Edit ${source.name}`}
                      onClick={() =>
                        setDraft({
                          id: source.id,
                          name: source.name,
                          baseUrl: String(source.config.baseUrl ?? ''),
                          email: String(source.config.email ?? ''),
                          jql: String(source.config.jql ?? ''),
                          browseJql: String(source.config.browseJql ?? ''),
                          plainSecret: '',
                        })
                      }
                    >
                      <Pencil className="size-3.5" aria-hidden />
                      <span className="sr-only">Edit {source.name}</span>
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      title={`Remove ${source.name}`}
                      onClick={() => remove.mutate(source.id)}
                    >
                      <Trash2 className="size-3.5" aria-hidden />
                      <span className="sr-only">Remove {source.name}</span>
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <ErrorNote error={remove.error} />

      <Dialog open={draft !== null} onOpenChange={(next) => !next && setDraft(null)}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editing ? `Edit ${draft?.name}` : 'New tracker'}</DialogTitle>
            <DialogDescription>
              The queue query decides what runs unattended. The picker query decides what you may start by
              hand from the Runs screen.
            </DialogDescription>
          </DialogHeader>
          {draft && (
            <form
              className="grid items-start gap-x-4 gap-y-3 sm:grid-cols-2"
              onSubmit={(event) => {
                event.preventDefault();
                save.mutate();
              }}
            >
              <Field label="Name" htmlFor="src-name">
                <Input
                  id="src-name"
                  required
                  autoFocus
                  placeholder="jira"
                  value={draft.name}
                  onChange={(event) => setDraft({ ...draft, name: event.target.value })}
                />
              </Field>
              <Field label="Jira base URL" htmlFor="src-url">
                <Input
                  id="src-url"
                  placeholder="https://site.atlassian.net"
                  value={draft.baseUrl}
                  onChange={(event) => setDraft({ ...draft, baseUrl: event.target.value })}
                />
              </Field>
              <Field label="Account email" htmlFor="src-email">
                <Input
                  id="src-email"
                  placeholder="you@company.com"
                  value={draft.email}
                  onChange={(event) => setDraft({ ...draft, email: event.target.value })}
                />
              </Field>
              <Field
                label="API token"
                htmlFor="src-token"
                hint={editing ? 'Leave empty to keep the stored one.' : 'Stored encrypted.'}
              >
                <Input
                  id="src-token"
                  type="password"
                  placeholder={editing ? 'unchanged' : 'stored encrypted'}
                  value={draft.plainSecret}
                  onChange={(event) => setDraft({ ...draft, plainSecret: event.target.value })}
                />
              </Field>
              <Field
                label="Queue query (JQL)"
                htmlFor="src-jql"
                className="sm:col-span-2"
                hint="What the worker imports and runs without anyone asking."
              >
                <Input
                  id="src-jql"
                  placeholder={'labels = agent AND status = "Ready"'}
                  value={draft.jql}
                  onChange={(event) => setDraft({ ...draft, jql: event.target.value })}
                />
              </Field>
              <Field
                label="Picker query (JQL)"
                htmlFor="src-browse"
                className="sm:col-span-2"
                hint="What you may choose from when starting one by hand."
              >
                <Input
                  id="src-browse"
                  placeholder="assignee = currentUser() AND statusCategory != Done"
                  value={draft.browseJql}
                  onChange={(event) => setDraft({ ...draft, browseJql: event.target.value })}
                />
              </Field>
              <div className="sm:col-span-2">
                <ErrorNote error={save.error} />
              </div>
              <div className="flex justify-end gap-2 border-t border-rule pt-3 sm:col-span-2">
                <Button type="button" variant="outline" onClick={() => setDraft(null)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={save.isPending}>
                  {editing ? 'Save changes' : 'Add tracker'}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </Panel>
  );
}

/**
 * What a first handler says, so that the empty form is not the thing standing
 * between an installation and its first run. It is a starting point rather than
 * a default: it arrives in the form to be read and edited, never saved unseen.
 * Everything the run harness already states — the worktree, the commit
 * conventions, verification, the BLOCKED protocol — is deliberately absent.
 */
export const STARTER_PROMPT = `Implement this task end to end, the way the person who filed it would if they knew this codebase.

Read before you write. Find the code the task names and the nearest existing example of the same kind of change, and follow that pattern rather than introducing a new one.

Keep the change to what was asked: no drive-by refactors, no renames, no new dependencies unless the task calls for them.

Cover it the way this repo covers things — if it has tests, add or update the ones that would have caught the bug, or that pin the behaviour you are adding.

If the task can be read two ways, take the reading a careful colleague would, carry on, and say which one you took in your summary.`;

function Handlers({ tenant, onChanged }: { tenant: TenantDetail; onChanged: () => void }) {
  const empty = { id: '', status: '', prompt: '', onSuccess: '', onFailure: '' };
  // A tenant with no handler at all cannot run anything, so its first one starts
  // written rather than blank; every one after that is a considered addition.
  const starter = { ...empty, status: '*', prompt: STARTER_PROMPT };
  const [draft, setDraft] = useState<typeof empty | null>(null);
  const editing = Boolean(draft?.id);

  const save = useMutation({
    mutationFn: () =>
      api.post(`/api/tenants/${tenant.id}/handlers`, {
        ...draft,
        id: draft?.id || undefined,
        onSuccess: draft?.onSuccess || null,
        onFailure: draft?.onFailure || null,
      }),
    onSuccess: () => {
      setDraft(null);
      onChanged();
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.del(`/api/tenants/${tenant.id}/handlers/${id}`),
    onSuccess: onChanged,
  });

  return (
    <Panel
      title="Handlers"
      description="What the agent should do with a task in a given status, and where the task moves afterwards. Use * as the catch-all."
      actions={
        <Button size="sm" onClick={() => setDraft(tenant.handlers.length === 0 ? starter : empty)}>
          <Plus className="size-3.5" aria-hidden /> Add handler
        </Button>
      }
    >
      {tenant.handlers.length === 0 ? (
        <EmptyState
          title="No handlers"
          hint={'A task cannot run until its status matches one. Start with a "*" handler that says "implement this task end to end".'}
          action={
            <Button size="sm" onClick={() => setDraft(starter)}>
              <Plus className="size-3.5" aria-hidden /> Add handler
            </Button>
          }
        />
      ) : (
        <div className="-my-1 divide-y divide-rule">
          {tenant.handlers.map((handler) => (
            <div key={handler.id} className="py-3 first:pt-1 last:pb-1">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="flex flex-wrap items-center gap-2">
                    <Badge variant={handler.status === '*' ? 'secondary' : 'default'}>{handler.status}</Badge>
                    <span className="text-[11.5px] text-ink-3">
                      → {handler.onSuccess ?? 'no transition'} on success, {handler.onFailure ?? 'no transition'} on failure
                    </span>
                  </p>
                  <p className="mt-1.5 max-w-[80ch] text-[12.5px] whitespace-pre-wrap text-ink-2">
                    {handler.prompt}
                  </p>
                </div>
                <div className="flex shrink-0 gap-1">
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    title={`Edit the ${handler.status} handler`}
                    onClick={() =>
                      setDraft({
                        id: handler.id,
                        status: handler.status,
                        prompt: handler.prompt,
                        onSuccess: handler.onSuccess ?? '',
                        onFailure: handler.onFailure ?? '',
                      })
                    }
                  >
                    <Pencil className="size-3.5" aria-hidden />
                    <span className="sr-only">Edit the {handler.status} handler</span>
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    title={`Delete the ${handler.status} handler`}
                    onClick={() => remove.mutate(handler.id)}
                  >
                    <Trash2 className="size-3.5" aria-hidden />
                    <span className="sr-only">Delete the {handler.status} handler</span>
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      <ErrorNote error={remove.error} />

      <Dialog open={draft !== null} onOpenChange={(next) => !next && setDraft(null)}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editing ? `Edit the ${draft?.status} handler` : 'New handler'}</DialogTitle>
            <DialogDescription>
              These instructions reach the agent as its brief. Nobody is watching while it works, so say what
              finished looks like.
            </DialogDescription>
          </DialogHeader>
          {draft && (
            <form
              className="grid items-start gap-x-4 gap-y-3 sm:grid-cols-3"
              onSubmit={(event) => {
                event.preventDefault();
                save.mutate();
              }}
            >
              <Field label="Task status" htmlFor="handler-status">
                <Input
                  id="handler-status"
                  required
                  autoFocus
                  placeholder="* or Ready for Agent"
                  value={draft.status}
                  onChange={(event) => setDraft({ ...draft, status: event.target.value })}
                />
              </Field>
              <Field label="Move to on success" htmlFor="handler-success">
                <Input
                  id="handler-success"
                  placeholder="In Review"
                  value={draft.onSuccess}
                  onChange={(event) => setDraft({ ...draft, onSuccess: event.target.value })}
                />
              </Field>
              <Field label="Move to on failure" htmlFor="handler-failure">
                <Input
                  id="handler-failure"
                  placeholder="Blocked"
                  value={draft.onFailure}
                  onChange={(event) => setDraft({ ...draft, onFailure: event.target.value })}
                />
              </Field>
              <Field
                label="Instructions for the agent"
                htmlFor="handler-prompt"
                hint="The run already tells the agent where it is, how to commit and how to report. This is the work itself."
                className="sm:col-span-3"
              >
                <Textarea
                  id="handler-prompt"
                  required
                  className="min-h-40"
                  value={draft.prompt}
                  onChange={(event) => setDraft({ ...draft, prompt: event.target.value })}
                />
              </Field>
              <div className="sm:col-span-3">
                <ErrorNote error={save.error} />
              </div>
              <div className="flex justify-end gap-2 border-t border-rule pt-3 sm:col-span-3">
                <Button type="button" variant="outline" onClick={() => setDraft(null)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={save.isPending}>
                  {editing ? 'Save changes' : 'Add handler'}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </Panel>
  );
}

function AgentSettings({ tenant, onChanged }: { tenant: TenantDetail; onChanged: () => void }) {
  const settings = tenant.settings;
  const [form, setForm] = useState({
    model: settings?.model ?? 'claude-opus-5',
    timeoutMinutes: String(settings?.timeoutMinutes ?? 45),
    maxRuntimeMinutes: String(settings?.maxRuntimeMinutes ?? 240),
    maxContinuations: String(settings?.maxContinuations ?? 2),
    maxParallel: String(settings?.maxParallel ?? 1),
    skills: (settings?.skills ?? []).join(', '),
    prompt: settings?.prompt ?? '',
    mcp: settings?.mcpServers ? JSON.stringify(settings.mcpServers, null, 2) : '',
    strictMcp: Boolean(settings?.strictMcp),
    gitUserName: settings?.gitUserName ?? '',
    gitUserEmail: settings?.gitUserEmail ?? '',
    notify: settings?.notify ?? 'off',
    notifyExtra: (settings?.notifyExtra ?? []).join(', '),
  });

  const save = useMutation({
    mutationFn: () => {
      let mcpServers: unknown = null;
      if (form.mcp.trim()) {
        try {
          mcpServers = JSON.parse(form.mcp);
        } catch {
          throw new Error('MCP servers must be valid JSON');
        }
      }
      return api.put(`/api/tenants/${tenant.id}/settings`, {
        model: form.model,
        timeoutMinutes: Number(form.timeoutMinutes),
        maxRuntimeMinutes: Number(form.maxRuntimeMinutes),
        maxContinuations: Number(form.maxContinuations),
        maxParallel: Number(form.maxParallel),
        skills: form.skills.split(',').map((skill) => skill.trim()).filter(Boolean),
        prompt: form.prompt || null,
        mcpServers,
        strictMcp: form.strictMcp,
        gitUserName: form.gitUserName.trim() || null,
        gitUserEmail: form.gitUserEmail.trim() || null,
        notify: form.notify,
        notifyExtra: form.notifyExtra
          .split(',')
          .map((address) => address.trim())
          .filter(Boolean),
      });
    },
    onSuccess: onChanged,
  });

  return (
    <Panel
      title="Agent"
      description="How this tenant's runs behave: which model, when a quiet agent counts as stuck, who they commit as, and which skills and MCP servers they can reach."
    >
      <div className="grid max-w-[60rem] items-start gap-x-4 gap-y-3 md:grid-cols-3">
        <div className="grid content-start gap-1.5">
          <Label htmlFor="agent-model">Model</Label>
          <Input
            id="agent-model"
            value={form.model}
            onChange={(event) => setForm({ ...form, model: event.target.value })}
          />
        </div>
        <div className="grid content-start gap-1.5">
          <Label htmlFor="agent-timeout">Stall timeout (minutes)</Label>
          <Input
            id="agent-timeout"
            value={form.timeoutMinutes}
            onChange={(event) => setForm({ ...form, timeoutMinutes: event.target.value })}
          />
          <p className="text-[11.5px] text-ink-3">
            How long the agent may go without writing anything before it counts as stuck. Work that is
            still producing output is left alone.
          </p>
        </div>
        <div className="grid content-start gap-1.5">
          <Label htmlFor="agent-ceiling">Ceiling (minutes)</Label>
          <Input
            id="agent-ceiling"
            value={form.maxRuntimeMinutes}
            onChange={(event) => setForm({ ...form, maxRuntimeMinutes: event.target.value })}
          />
          <p className="text-[11.5px] text-ink-3">
            The longest a run may take however busy it looks — the stop on an agent going round in
            circles.
          </p>
        </div>
        <div className="grid content-start gap-1.5">
          <Label htmlFor="agent-continuations">Continuations</Label>
          <Input
            id="agent-continuations"
            value={form.maxContinuations}
            onChange={(event) => setForm({ ...form, maxContinuations: event.target.value })}
          />
          <p className="text-[11.5px] text-ink-3">
            How often a task stopped at the ceiling with work committed may be picked up again, on the same
            branch. 0 never continues.
          </p>
        </div>
        <div className="grid content-start gap-1.5">
          <Label htmlFor="agent-parallel">Runs at once</Label>
          <Input
            id="agent-parallel"
            value={form.maxParallel}
            onChange={(event) => setForm({ ...form, maxParallel: event.target.value })}
          />
        </div>
        <div className="grid content-start gap-1.5">
          <Label htmlFor="agent-git-name">Commit author</Label>
          <Input
            id="agent-git-name"
            placeholder="taskpilot"
            value={form.gitUserName}
            onChange={(event) => setForm({ ...form, gitUserName: event.target.value })}
          />
        </div>
        <div className="grid content-start gap-1.5 md:col-span-2">
          <Label htmlFor="agent-git-email">Commit author email</Label>
          <Input
            id="agent-git-email"
            placeholder="taskpilot@localhost"
            value={form.gitUserEmail}
            onChange={(event) => setForm({ ...form, gitUserEmail: event.target.value })}
          />
          <p className="text-[12px] text-ink-2">
            The tenant's fallback author: used for work a tracker raised, and for members who have not set
            their own under Account. Left empty, those runs commit as taskpilot — the agent is never allowed
            to pick a name for itself.
          </p>
        </div>
        <div className="grid content-start gap-1.5">
          <Label htmlFor="agent-notify">Email about runs</Label>
          <select
            id="agent-notify"
            value={form.notify}
            onChange={(event) => setForm({ ...form, notify: event.target.value })}
            className={SELECT}
          >
            <option value="off">never</option>
            <option value="failures">only when a run fails</option>
            <option value="all">every finished run</option>
          </select>
        </div>
        <div className="grid content-start gap-1.5 md:col-span-2">
          <Label htmlFor="agent-notify-extra">Also email</Label>
          <Input
            id="agent-notify-extra"
            placeholder="ops@example.com, oncall@example.com"
            value={form.notifyExtra}
            onChange={(event) => setForm({ ...form, notifyExtra: event.target.value })}
          />
          <p className="text-[12px] text-ink-2">
            Whoever queued the task is told either way. These addresses are told as well —
            useful for work a tracker raised, which nobody queued by hand. Needs mail configured
            on the installation.
          </p>
        </div>
        <div className="grid content-start gap-1.5 md:col-span-3">
          <Label htmlFor="agent-skills">Skills</Label>
          <Input
            id="agent-skills"
            placeholder="/livit-shared:pr-conventions, /livit-shared:testing-patterns"
            value={form.skills}
            onChange={(event) => setForm({ ...form, skills: event.target.value })}
          />
        </div>
        <div className="grid content-start gap-1.5 md:col-span-3">
          <Label htmlFor="agent-prompt">House rules added to every prompt</Label>
          <Textarea
            id="agent-prompt"
            value={form.prompt}
            onChange={(event) => setForm({ ...form, prompt: event.target.value })}
          />
        </div>
        <div className="grid content-start gap-1.5 md:col-span-3">
          <Label htmlFor="agent-mcp">MCP servers (JSON)</Label>
          <Textarea
            id="agent-mcp"
            placeholder={'{\n  "atlassian": { "command": "npx", "args": ["-y", "mcp-atlassian"] }\n}'}
            value={form.mcp}
            onChange={(event) => setForm({ ...form, mcp: event.target.value })}
          />
          <p className="text-[12px] text-ink-2">
            Only this tenant's runs can reach these servers — work credentials stay out of other tenants.
          </p>
        </div>
        <label className="flex items-start gap-2.5 text-sm md:col-span-3">
          <input
            type="checkbox"
            checked={form.strictMcp}
            onChange={(event) => setForm({ ...form, strictMcp: event.target.checked })}
            className="mt-0.5 size-4"
          />
          Ignore the host machine's own MCP configuration
        </label>
      </div>
      <div className="mt-4">
        <Button onClick={() => save.mutate()} disabled={save.isPending}>
          Save agent settings
        </Button>
        <ErrorNote error={save.error} />
      </div>
    </Panel>
  );
}

function DeployKey({ tenant, onChanged }: { tenant: TenantDetail; onChanged: () => void }) {
  const [notice, setNotice] = useState('');
  const [keyType, setKeyType] = useState<'ed25519' | 'rsa'>('ed25519');
  const create = useMutation({
    mutationFn: () => api.post<{ publicKey: string }>(`/api/tenants/${tenant.id}/ssh-key`, { type: keyType }),
    onSuccess: () => {
      setNotice('New key generated. Add the public half to this tenant’s repositories.');
      onChanged();
    },
  });
  const rescan = useMutation({
    mutationFn: () => api.post<{ hosts: number }>(`/api/tenants/${tenant.id}/known-hosts`),
    onSuccess: (result) => {
      setNotice(`Pinned ${result.hosts} host key(s).`);
      onChanged();
    },
  });

  return (
    <Panel
      title="Git access"
      description="This tenant clones and pushes with its own deploy key, never the host machine's credentials. The private half is encrypted and only written inside this tenant's run directory. Azure DevOps accepts RSA keys only; everywhere else prefer ed25519."
    >
      {tenant.settings?.sshPublicKey ? (
        <pre className="overflow-auto rounded-[5px] border border-rule bg-field px-3 py-2 font-mono text-[11.5px] break-all whitespace-pre-wrap text-ink-2">
          {tenant.settings.sshPublicKey}
        </pre>
      ) : (
        <EmptyState
          title="No deploy key"
          hint="Without one, clones fall back to whatever credentials the host happens to have."
        />
      )}
      <ForgeToken tenant={tenant} onChanged={onChanged} />

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <select
          value={keyType}
          onChange={(event) => setKeyType(event.target.value as 'ed25519' | 'rsa')}
          aria-label="Key type"
          className="h-8 rounded-[5px] border border-rule bg-field px-2.5 text-[13px] text-ink outline-none transition-[border-color,box-shadow] hover:border-rule-strong focus-visible:border-brand focus-visible:ring-2 focus-visible:ring-brand/25"
        >
          <option value="ed25519">ed25519 — GitHub, GitLab, Bitbucket</option>
          <option value="rsa">RSA 4096 — required by Azure DevOps</option>
        </select>
        <Button onClick={() => create.mutate()} disabled={create.isPending}>
          <KeyRound className="size-3.5" aria-hidden />
          {tenant.settings?.hasSshKey ? 'Rotate key' : 'Generate key'}
        </Button>
        <Button variant="outline" onClick={() => rescan.mutate()} disabled={rescan.isPending}>
          <RefreshCw className="size-3.5" aria-hidden /> Re-pin host keys
        </Button>
        {tenant.settings?.sshPublicKey && (
          <Button
            variant="outline"
            onClick={() => void navigator.clipboard.writeText(tenant.settings!.sshPublicKey!)}
          >
            <Copy className="size-3.5" aria-hidden /> Copy public key
          </Button>
        )}
        {notice && <span className="text-[12px] text-ink-2">{notice}</span>}
      </div>
      <ErrorNote error={create.error ?? rescan.error} />
    </Panel>
  );
}

function ForgeToken({ tenant, onChanged }: { tenant: TenantDetail; onChanged: () => void }) {
  const [token, setToken] = useState('');
  const save = useMutation({
    mutationFn: () => api.put(`/api/tenants/${tenant.id}/settings`, { forgeToken: token }),
    onSuccess: () => {
      setToken('');
      onChanged();
    },
  });

  return (
    <div className="mt-5 border-t border-rule pt-4">
      <p className="label-caps mb-1.5">Merge requests</p>
      <p className="mb-3 max-w-[80ch] text-[12px] text-ink-2">
        The deploy key pushes the branch; opening the merge request needs an API token — a GitHub PAT, a GitLab
        token, or an Azure DevOps PAT with Code (read &amp; write). Stored encrypted.{' '}
        {tenant.settings?.hasForgeToken ? 'A token is set.' : 'No token yet.'}
      </p>
      <form
        className="flex flex-wrap items-end gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          save.mutate();
        }}
      >
        <div className="grid min-w-64 flex-1 gap-1.5">
          <Label htmlFor={`forge-${tenant.id}`}>API token</Label>
          <Input
            id={`forge-${tenant.id}`}
            type="password"
            placeholder={tenant.settings?.hasForgeToken ? 'replace the stored token' : 'paste the token'}
            value={token}
            onChange={(event) => setToken(event.target.value)}
          />
        </div>
        <Button type="submit" disabled={!token.trim() || save.isPending}>
          Save token
        </Button>
      </form>
      <ErrorNote error={save.error} />
    </div>
  );
}

function Members({ tenant }: { tenant: TenantDetail }) {
  const client = useQueryClient();
  const access = useQuery({
    queryKey: ['members', tenant.id],
    queryFn: () =>
      api.get<{ members: MemberRow[]; invitations: InvitationRow[] }>(`/api/tenants/${tenant.id}/members`),
  });
  const refresh = () => client.invalidateQueries({ queryKey: ['members', tenant.id] });

  const [email, setEmail] = useState('');
  const [role, setRole] = useState('member');
  const [notice, setNotice] = useState('');

  const add = useMutation({
    mutationFn: () =>
      api.post<{
        outcome: 'granted' | 'role-changed' | 'invited';
        email: string;
        acceptUrl?: string;
        emailed?: boolean;
      }>(`/api/tenants/${tenant.id}/members`, { email, role }),
    onSuccess: (result) => {
      // Grant or invite is the server's call, not a decision for whoever typed
      // the address — so its answer is what gets reported. An invitation shows
      // its link either way: mail can be off, and a provider can refuse.
      setNotice(
        result.outcome === 'granted'
          ? `${result.email} now has access.`
          : result.outcome === 'role-changed'
            ? `${result.email} is now a ${role}.`
            : result.emailed
              ? `No account yet — invitation emailed to ${result.email}. The link, in case it does not arrive: ${result.acceptUrl}`
              : `No account yet — send this invitation link yourself: ${result.acceptUrl}`,
      );
      setEmail('');
      refresh();
    },
  });
  const remove = useMutation({
    mutationFn: (memberId: string) => api.del(`/api/tenants/${tenant.id}/members/${memberId}`),
    onSuccess: refresh,
  });
  const resend = useMutation({
    mutationFn: (invitationId: string) =>
      api.post<{ email: string; acceptUrl: string; emailed: boolean }>(
        `/api/tenants/${tenant.id}/members/invitations/${invitationId}/resend`,
      ),
    onSuccess: (result) => {
      setNotice(
        result.emailed
          ? `Sent again to ${result.email}, valid for another week.`
          : `Not emailed — send this link yourself: ${result.acceptUrl}`,
      );
      refresh();
    },
  });

  return (
    <Panel
      title="People"
      description="Admins configure the tenant, members queue and run tasks, viewers can only look."
    >
      {access.isPending ? (
        <Skeleton className="h-20 w-full" />
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Person</TableHead>
              <TableHead>Role</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(access.data?.members ?? []).map((member) => (
              <TableRow key={member.id}>
                <TableCell className="data font-semibold text-ink">{member.email}</TableCell>
                <TableCell className="data text-ink-2">{member.role}</TableCell>
                <TableCell className="text-right">
                  {member.role !== 'owner' && (
                    <Button variant="ghost" size="sm" onClick={() => remove.mutate(member.id)}>
                      <Trash2 className="size-3.5" aria-hidden />
                      <span className="sr-only">Revoke access for {member.email}</span>
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {(access.data?.invitations ?? []).map((invitation) => (
              <TableRow key={invitation.id}>
                <TableCell className="data text-ink-2">{invitation.email}</TableCell>
                <TableCell className="data text-ink-3">invited · {invitation.role}</TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      title="Send it again"
                      disabled={resend.isPending}
                      onClick={() => resend.mutate(invitation.id)}
                    >
                      <Send className="size-3.5" aria-hidden />
                      <span className="sr-only">Send the invitation to {invitation.email} again</span>
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      title="Copy the invitation link"
                      onClick={() =>
                        void navigator.clipboard.writeText(`${location.origin}/accept/${invitation.id}`)
                      }
                    >
                      <Copy className="size-3.5" aria-hidden />
                      <span className="sr-only">Copy the invitation link</span>
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <form
        className="mt-5 grid max-w-[60rem] items-start gap-x-4 gap-y-3 border-t border-rule pt-5 md:grid-cols-[minmax(0,1fr)_14rem_auto]"
        onSubmit={(event) => {
          event.preventDefault();
          add.mutate();
        }}
      >
        <Field
          label="Email"
          htmlFor="member-email"
          hint="An account that exists is given access now. One that does not is invited, and the invitation creates it when they accept."
        >
          <Input
            id="member-email"
            type="email"
            required
            placeholder="colleague@example.com"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </Field>
        <Field label="Role" htmlFor="member-role">
          <select
            id="member-role"
            value={role}
            onChange={(event) => setRole(event.target.value)}
            className={SELECT}
          >
            <option value="viewer">viewer — read only</option>
            <option value="member">member — queue and run</option>
            <option value="admin">admin — configure</option>
          </select>
        </Field>
        <div className="flex items-start">
          <Button type="submit" disabled={add.isPending}>
            <Plus className="size-3.5" aria-hidden /> Add person
          </Button>
        </div>
        {notice && <p className="data break-all text-ink-2 md:col-span-3">{notice}</p>}
        <div className="md:col-span-3">
          <ErrorNote error={add.error} />
        </div>
      </form>
    </Panel>
  );
}
